import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type {
  DocumentPatchResult,
  StrategyConfigView,
  StrategyDetail,
  StrategyGraph,
  StrategyLayoutView,
  StrategyPatchOp,
  StrategySummary,
  StrategyValidateResult,
  StrategyYamlView,
} from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";
import ManualPromptsCard from "@/components/ManualPromptsCard";
import FlowchartView, { FlowchartLegend } from "@/components/strategy/FlowchartView";
import PhaseInspector from "@/components/strategy/PhaseInspector";
import StrategyOverviewCards from "@/components/strategy/StrategyOverviewCards";
import { applyOpsToDocument, upsertOp } from "@/lib/strategyDraft";
import { useUnsavedGuard } from "@/lib/useUnsavedGuard";
import type { PositionOverrides } from "@/lib/graphLayout";
import { formatDateTime } from "@/lib/format";
import { useAuth } from "@/lib/auth";

const INPUT_CLS =
  "w-full rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400";

const UNSAVED_MESSAGE =
  "You have unsaved strategy changes. Leave anyway and lose them?";

/**
 * Strategy editor (deliverable 2.8).
 *
 * <p>The flowchart is the map: phases and transitions, draggable into place.
 * Every configured field — strategy-wide cards above, per-phase inspector
 * beside the chart — is editable in place. Edits accumulate into one draft
 * (patch ops + dragged positions) and are saved together from the header;
 * leaving the page with unsaved changes warns first. The raw YAML editors
 * remain as the escape hatch for structural changes.
 */
export default function StrategyEditorPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const roles = user?.roles ?? [];
  const canEdit = roles.some((r) => r === "ADMIN" || r === "OPERATOR");
  const isAdmin = roles.includes("ADMIN");

  const strategiesQ = useQuery({
    queryKey: ["strategies"],
    queryFn: () => api.get<StrategySummary[]>("/api/v1/strategies"),
  });

  const [slug, setSlug] = useState<string | null>(null);
  useEffect(() => {
    if (slug) return;
    const first = strategiesQ.data?.[0];
    if (first) setSlug(first.slug);
  }, [strategiesQ.data, slug]);

  const detailQ = useQuery({
    queryKey: ["strategy-detail", slug],
    enabled: !!slug,
    queryFn: () => api.get<StrategyDetail>(`/api/v1/strategies/${slug}`),
  });

  const graphQ = useQuery({
    queryKey: ["strategy-graph", slug],
    enabled: !!slug,
    queryFn: () => api.get<StrategyGraph>(`/api/v1/strategies/${slug}/graph`),
  });

  const layoutQ = useQuery({
    queryKey: ["strategy-layout", slug],
    enabled: !!slug,
    queryFn: () => api.get<StrategyLayoutView>(`/api/v1/strategies/${slug}/layout`),
  });

  // The parsed document (original YAML keys) drives the field editors. Admin
  // only — it is the whole client-authored strategy, same as the raw YAML.
  const configQ = useQuery({
    queryKey: ["strategy-config", slug],
    enabled: !!slug && isAdmin,
    queryFn: () => api.get<StrategyConfigView>(`/api/v1/strategies/${slug}/config`),
  });

  const yamlQ = useQuery({
    queryKey: ["strategy-yaml", slug],
    enabled: !!slug && isAdmin,
    queryFn: () => api.get<StrategyYamlView>(`/api/v1/strategies/${slug}/yaml`),
  });

  // ---- Draft state -------------------------------------------------------

  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [pendingOps, setPendingOps] = useState<StrategyPatchOp[]>([]);
  const [layoutDraft, setLayoutDraft] = useState<PositionOverrides | null>(null);
  const [draftGraph, setDraftGraph] = useState<StrategyGraph | null>(null);
  const [yaml, setYaml] = useState("");
  const [validation, setValidation] = useState<StrategyValidateResult | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setYaml(yamlQ.data?.yaml ?? "");
    setValidation(null);
    setDraftGraph(null);
  }, [yamlQ.data?.yaml, slug]);

  useEffect(() => {
    setSelectedSlug(null);
    setPendingOps([]);
    setLayoutDraft(null);
    setSaveError(null);
  }, [slug]);

  const yamlDirty = isAdmin && yaml !== (yamlQ.data?.yaml ?? "");
  const fieldsDirty = pendingOps.length > 0;
  const layoutDirty = layoutDraft !== null;
  const dirty = fieldsDirty || layoutDirty || yamlDirty;
  const dirtyCount =
    pendingOps.length + (layoutDirty ? 1 : 0) + (yamlDirty ? 1 : 0);

  useUnsavedGuard(dirty, UNSAVED_MESSAGE);

  const draftDocument = useMemo(() => {
    if (!configQ.data) return null;
    return fieldsDirty
      ? applyOpsToDocument(configQ.data.document, pendingOps)
      : configQ.data.document;
  }, [configQ.data, pendingOps, fieldsDirty]);

  const savedPositions = layoutQ.data?.layout?.positions;
  const effectivePositions = useMemo<PositionOverrides | undefined>(() => {
    if (!savedPositions && !layoutDraft) return undefined;
    return { ...savedPositions, ...layoutDraft };
  }, [savedPositions, layoutDraft]);

  // ---- Mutations ---------------------------------------------------------

  const validateM = useMutation({
    mutationFn: (yamlText: string) =>
      api.post<StrategyValidateResult>("/api/v1/strategies/validate", { yaml: yamlText }),
    onSuccess: (r) => {
      setValidation(r);
      setDraftGraph(r.valid ? r.graph : null);
    },
  });

  const saveYamlM = useMutation({
    mutationFn: (yamlText: string) =>
      api.post<StrategySummary>("/api/v1/strategies", { yaml: yamlText }),
    onSuccess: () => {
      setValidation({ valid: true, error: null, graph: null });
      setDraftGraph(null);
      refreshStrategy();
    },
  });

  const saveAllM = useMutation({
    mutationFn: async () => {
      if (fieldsDirty) {
        if (!configQ.data) throw new Error("document not loaded");
        const result = await api.put<DocumentPatchResult>(
          `/api/v1/strategies/${slug}/document`,
          { baseHash: configQ.data.configHash, ops: pendingOps },
        );
        if (!result.valid) {
          throw new ValidationFailed(result.error ?? "the edited document does not validate");
        }
      }
      if (layoutDirty) {
        await api.put(`/api/v1/strategies/${slug}/layout`, {
          positions: { ...savedPositions, ...layoutDraft },
        });
      }
    },
    onSuccess: () => {
      setPendingOps([]);
      setLayoutDraft(null);
      setSaveError(null);
      refreshStrategy();
    },
    onError: (e) => {
      if (e instanceof ValidationFailed) setSaveError(e.message);
      else if (e instanceof ApiError && e.status === 409) {
        setSaveError(
          "The strategy changed since this page loaded (another editor or a YAML upload). " +
          "Reload the page and re-apply your changes.",
        );
      } else setSaveError("Save failed. Check the connection and try again.");
    },
  });

  function refreshStrategy() {
    queryClient.invalidateQueries({ queryKey: ["strategies"] });
    queryClient.invalidateQueries({ queryKey: ["strategy-detail", slug] });
    queryClient.invalidateQueries({ queryKey: ["strategy-graph", slug] });
    queryClient.invalidateQueries({ queryKey: ["strategy-yaml", slug] });
    queryClient.invalidateQueries({ queryKey: ["strategy-config", slug] });
    queryClient.invalidateQueries({ queryKey: ["strategy-layout", slug] });
    queryClient.invalidateQueries({ queryKey: ["phase-yaml", slug] });
  }

  function discardAll() {
    setPendingOps([]);
    setLayoutDraft(null);
    setSaveError(null);
    setYaml(yamlQ.data?.yaml ?? "");
    setValidation(null);
    setDraftGraph(null);
  }

  const onFieldEdit = (op: StrategyPatchOp) => {
    setPendingOps((ops) => upsertOp(ops, op));
    setSaveError(null);
  };

  const shownGraph = draftGraph ?? graphQ.data ?? null;

  const orderedPhases = useMemo(() => {
    if (!detailQ.data) return [];
    return [...detailQ.data.phases].sort((a, b) => a.orderIndex - b.orderIndex);
  }, [detailQ.data]);

  const selectedNode = useMemo(
    () => shownGraph?.nodes.find((n) => n.slug === selectedSlug) ?? null,
    [shownGraph, selectedSlug],
  );
  const selectedPhaseId = useMemo(
    () => orderedPhases.find((p) => p.slug === selectedSlug)?.id ?? null,
    [orderedPhases, selectedSlug],
  );

  // Edits the chart is drawn from: it redraws from the saved graph on save.
  const chartEdited = pendingOps.some((o) =>
    ["transitions", "transition_rules", "order", "name"].includes(o.keyPath[0] ?? ""),
  );

  if (strategiesQ.isLoading) return <div className="text-sm text-zinc-500">Loading…</div>;
  if (!strategiesQ.data || strategiesQ.data.length === 0) {
    return <NoStrategy onUpload={(y) => saveYamlM.mutate(y)} />;
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Strategies</h1>
          <p className="text-sm text-[var(--fg-muted)]">
            Everything the agent is configured to do. Click a value to change it; drag
            phases to arrange the chart. Nothing is applied until you save.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-[var(--fg-muted)]" htmlFor="strategy-select">
            Strategy
          </label>
          <select
            id="strategy-select"
            value={slug ?? ""}
            onChange={(e) => {
              if (dirty && !window.confirm(UNSAVED_MESSAGE)) return;
              setSlug(e.target.value);
            }}
            className="rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-2 py-1 text-sm"
          >
            {strategiesQ.data.map((s) => (
              <option key={s.id} value={s.slug}>{s.name}</option>
            ))}
          </select>

          {isAdmin ? (
            <>
              {dirty ? (
                <span
                  role="status"
                  className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-800"
                >
                  {dirtyCount} unsaved change{dirtyCount === 1 ? "" : "s"}
                </span>
              ) : null}
              <button
                type="button"
                disabled={!fieldsDirty && !layoutDirty}
                onClick={discardAll}
                className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50 disabled:opacity-50"
              >
                Discard
              </button>
              <button
                type="button"
                disabled={(!fieldsDirty && !layoutDirty) || saveAllM.isPending}
                onClick={() => saveAllM.mutate()}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saveAllM.isPending ? "Saving…" : "Save changes"}
              </button>
            </>
          ) : null}
        </div>
      </header>

      {saveError ? (
        <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          {saveError}
        </div>
      ) : null}

      {isAdmin && draftDocument ? (
        <StrategyOverviewCards
          document={draftDocument}
          pendingOps={pendingOps}
          onEdit={onFieldEdit}
        />
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_24rem]">
        <Card className="min-w-0">
          <div className="flex items-end justify-between gap-3 mb-3 flex-wrap">
            <CardTitle>Phase flowchart</CardTitle>
            {detailQ.data ? (
              <div className="text-xs text-[var(--fg-muted)]">
                version {detailQ.data.strategy.version} · updated{" "}
                {formatDateTime(detailQ.data.strategy.updatedAt)}
              </div>
            ) : null}
          </div>

          {draftGraph ? (
            <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Previewing unsaved changes from the YAML editor. Save to apply them.
            </div>
          ) : null}
          {chartEdited ? (
            <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Edits to phases or transitions are pending — the chart redraws when you save.
            </div>
          ) : null}

          {graphQ.isLoading ? (
            <p className="text-sm text-zinc-500">Loading…</p>
          ) : graphQ.error ? (
            <p className="text-sm text-[var(--danger)]">Could not load the phase graph.</p>
          ) : shownGraph ? (
            <>
              <FlowchartView
                graph={shownGraph}
                selectedSlug={selectedSlug}
                onSelect={(s) => setSelectedSlug((current) => (current === s ? null : s))}
                className="max-h-[70vh]"
                positions={effectivePositions}
                onMove={
                  isAdmin
                    ? (nodeSlug, pos) =>
                        setLayoutDraft((d) => ({ ...(d ?? {}), [nodeSlug]: pos }))
                    : undefined
                }
              />
              <FlowchartLegend />
            </>
          ) : null}
        </Card>

        <div className="min-w-0 space-y-4">
          {selectedNode && slug ? (
            <>
              <PhaseInspector
                strategySlug={slug}
                strategyId={detailQ.data?.strategy.id ?? null}
                node={selectedNode}
                outgoing={shownGraph?.edges.filter((e) => e.from === selectedNode.slug) ?? []}
                incoming={shownGraph?.edges.filter((e) => e.to === selectedNode.slug) ?? []}
                canEditYaml={isAdmin && !draftGraph}
                onSaved={refreshStrategy}
                document={isAdmin ? draftDocument : null}
                pendingOps={pendingOps}
                onEdit={onFieldEdit}
              />
              {detailQ.data && selectedPhaseId ? (
                <ManualPromptsCard
                  strategyId={detailQ.data.strategy.id}
                  phases={orderedPhases}
                  canEdit={canEdit}
                  selectedPhaseId={selectedPhaseId}
                />
              ) : null}
            </>
          ) : (
            <Card>
              <CardTitle>No phase selected</CardTitle>
              <p className="text-sm text-[var(--fg-muted)]">
                Select a phase in the flowchart to edit its goal, pacing, email script,
                transitions, questions, computed fields and document output, and to inject
                guidance into the emails it writes.
              </p>
            </Card>
          )}
        </div>
      </div>

      {isAdmin ? (
        <Card>
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <CardTitle>Full strategy YAML</CardTitle>
              <p className="text-xs text-[var(--fg-muted)] max-w-2xl">
                The whole document, for structural changes — adding, removing or renaming
                phases, prompt overrides, the prospect schema. Validate to preview the
                resulting flowchart before saving.
              </p>
            </div>
            {yamlQ.isLoading ? (
              <span className="text-xs text-[var(--fg-muted)]">Loading…</span>
            ) : yamlDirty ? (
              <span className="text-xs text-amber-700">Unsaved changes</span>
            ) : null}
          </div>

          {fieldsDirty ? (
            <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Save or discard your field edits before saving from the YAML editor — a YAML
              save would replace the document they were made against.
            </div>
          ) : null}

          {/*
            The editor stays mounted while the document loads. Swapping it for a
            placeholder would remount the textarea and throw away anything typed
            into it the moment a refetch settles.
          */}
          <>
              <textarea
                aria-label="Strategy YAML"
                disabled={yamlQ.isLoading}
                value={yaml}
                onChange={(e) => {
                  setYaml(e.target.value);
                  setValidation(null);
                  setDraftGraph(null);
                }}
                rows={20}
                spellCheck={false}
                className="mt-3 w-full rounded-md border border-[var(--border)] bg-[var(--bg-card)] p-2 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <button
                  type="button"
                  disabled={!yaml || validateM.isPending}
                  onClick={() => validateM.mutate(yaml)}
                  className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50 disabled:opacity-50"
                >
                  {validateM.isPending ? "Validating…" : "Validate & preview"}
                </button>
                <button
                  type="button"
                  disabled={
                    !yaml || !yamlDirty || fieldsDirty ||
                    (validation !== null && !validation.valid) ||
                    saveYamlM.isPending
                  }
                  onClick={() => saveYamlM.mutate(yaml)}
                  className="rounded-md bg-blue-600 text-white px-3 py-1.5 text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  {saveYamlM.isPending ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  disabled={!yamlDirty || saveYamlM.isPending}
                  onClick={() => {
                    setYaml(yamlQ.data?.yaml ?? "");
                    setValidation(null);
                    setDraftGraph(null);
                  }}
                  className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50 disabled:opacity-50"
                >
                  Revert
                </button>
                {validation ? (
                  <span
                    className={
                      "text-sm " +
                      (validation.valid ? "text-[var(--success)]" : "text-[var(--danger)]")
                    }
                  >
                    {validation.valid ? "YAML is valid." : `Invalid: ${validation.error}`}
                  </span>
                ) : null}
                {saveYamlM.error ? (
                  <span className="text-sm text-[var(--danger)]">
                    {saveYamlM.error instanceof ApiError
                      ? `Save failed: ${saveYamlM.error.status}`
                      : "Save failed"}
                  </span>
                ) : null}
                {saveYamlM.isSuccess ? (
                  <span className="text-sm text-[var(--success)]">Saved.</span>
                ) : null}
              </div>
          </>
        </Card>
      ) : null}
    </div>
  );
}

class ValidationFailed extends Error {}

function NoStrategy({ onUpload }: { onUpload: (yaml: string) => void }) {
  const [yaml, setYaml] = useState("");
  return (
    <Card>
      <CardTitle>Create your first strategy</CardTitle>
      <p className="text-sm text-[var(--fg-muted)] mb-3">
        Paste a strategy YAML to get started. The platform's StrategyYamlLoader validates the
        schema; phases, transitions, and prospect schema are all configured here.
      </p>
      <textarea
        aria-label="Strategy YAML"
        rows={20}
        value={yaml}
        onChange={(e) => setYaml(e.target.value)}
        className={INPUT_CLS}
      />
      <button
        type="button"
        onClick={() => onUpload(yaml)}
        disabled={!yaml}
        className="mt-3 rounded-md bg-blue-600 text-white px-3 py-1.5 text-sm hover:bg-blue-700 disabled:opacity-50"
      >
        Upload
      </button>
    </Card>
  );
}
